import { useTranslation } from 'react-i18next';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { formatFlightDateTime } from '@/lib/disruptions/airport-tz';
import type { AssignedOnward } from './reaccommodation-diagram';
import { FieldLabel } from './airport-ui';

// The Flight tab's fact strip under the re-accommodation rail: the four facts an
// operator is asked at the counter — ticket, seat, recovery, onward — as
// boarding-pass fields. The rail tells the story; this states the numbers ONCE.
// Presentational.

function Field({ label, value, sub, mono }: { label: string; value: string; sub?: string | undefined; mono?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <span className={mono ? 'font-mono text-sm font-semibold tabular-nums' : 'text-sm font-semibold'}>{value}</span>
      {sub ? <span className="text-xs text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

export function FlightDetails({
  group,
  isTicketed,
  ticketNumber,
  recovery,
  onward,
  finalDestination,
}: {
  group: DisruptionPnrGroupRow;
  isTicketed: boolean;
  ticketNumber?: string | undefined;
  recovery?: { label: string; warn: boolean } | null | undefined;
  /** Onward-connection feasibility when the assigned option only reaches a hub. */
  onward?: AssignedOnward | null | undefined;
  finalDestination: string;
}) {
  const { t } = useTranslation('pnr');
  const seat = group.assignedFlight?.seat;
  const cabin = group.assignedFlight?.cabin;
  const onwardValue = onward?.onwardFlightNumber
    ? `${onward.onwardFlightNumber}${onward.onwardDepartureUtc ? ` · ${formatFlightDateTime(onward.onwardDepartureUtc, onward.onwardOrigin)}` : ''}`
    : t('flightDetails.onwardNone');
  const onwardSub = onward
    ? onward.connectionFeasible === undefined
      ? t('flightDetails.onwardUnknown')
      : onward.connectionFeasible
        ? t('flightDetails.onwardFeasible')
        : t('flightDetails.onwardTight')
    : t('flightDetails.onwardFinal', { code: finalDestination });

  return (
    <section
      className="grid grid-cols-2 gap-4 rounded-lg border border-border p-4 md:grid-cols-4"
      data-testid="flight-details"
    >
      <Field
        label={t('flightDetails.ticket')}
        value={isTicketed && ticketNumber ? ticketNumber : t('flightDetails.ticketPending')}
        sub={isTicketed ? t('flightDetails.issuedHint') : t('flightDetails.ticketPendingHint')}
        mono={isTicketed && !!ticketNumber}
      />
      <Field
        label={t('flightDetails.seat')}
        value={seat ?? t('flightDetails.seatAtCheckIn')}
        sub={cabin ? t('flightDetails.cabin', { cabin: cabin.toLowerCase() }) : t('flightDetails.cabinUnknown')}
      />
      <Field
        label={t('flightDetails.recovery')}
        value={recovery?.label ?? t('flightDetails.recoveryNone')}
        sub={recovery ? (recovery.warn ? t('flightDetails.outsideWindow') : t('flightDetails.withinWindow')) : undefined}
      />
      <Field label={t('flightDetails.onward')} value={onwardValue} sub={onwardSub} mono={!!onward?.onwardFlightNumber} />
    </section>
  );
}
